// src/pages/Watchlists.jsx
import { useMemo, useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import {
  MoreHorizontal, Search as SearchIcon,
  ChevronDown, Trash2
} from "lucide-react";
import WatchlistItemSidebar from "../components/DetailSidebarWatchlists";
import {
  ensureAuthOrRedirect,
  attachExpiryTimer,
  redirectToLogin,
} from "../utils/auth";

const TYPE_OPTIONS = [
  { key: "domain",        label: "Domain" },
  { key: "email",         label: "Email" },
  { key: "ip",            label: "IP Address" },
  { key: "computername",  label: "Computer Name" },
  { key: "github_repo",  label: "Github Repository" },
  { key: "keyword",  label: "Keyword" },
];

// Which audiences to alert (maps to corporate_alerts / consumer_alerts)
const DATA_OPTIONS = [
  { key: "both",      label: "Corporate + Consumer" },
  { key: "corporate", label: "Corporate only" },
  { key: "consumer",  label: "Consumer only" },
];

const placeholders = {
  domain: "E.g. example.com",
  keyword: "E.g. tesla",
  ip: "E.g. 192.x.x5",
  computername: "E.g. HOST-XYZ",
  email: 'E.g elonmusk@gmail.com',
  git_repo: 'E.g https://github.com/OWASP/wrongsecrets.git'
};

// Denser SOC/Datadog-style panel. Matches Profile / Organizations.
const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";

const inputClass =
  "w-full rounded-md border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-[13px] text-white outline-none focus:border-sky-500/50 transition-colors disabled:opacity-50";

const primaryBtn =
  "inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

const secondaryBtn =
  "inline-flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-900/40 hover:bg-neutral-900 px-3.5 py-1.5 text-[13px] font-medium text-neutral-200 transition-colors";

const fieldLabel = "block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium";

export default function Watchlists() {
  // Layout/Sidebar
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState("watchlists");
  const [activeTab, setActiveTab] = useState("settings");
  const [listError, setListError] = useState(null);
const [listSuccess, setListSuccess] = useState(null)
   const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
const [credits, setCredits] = useState(null);
  // Data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters/search
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all"); // enabled | disabled | all
  const [type, setType] = useState("all");

// track which row is being deleted (to disable the button)
const [deletingId, setDeletingId] = useState(null);

  // Create identifier form (right panel)
  const [newType, setNewType] = useState("domain");
  const [newEntry, setNewEntry] = useState("");
  const [recordType, setRecordType] = useState("both"); // maps to corporate/consumer toggles
  const [ignored, setIgnored] = useState("");           // used as notify_email (fallback on backend)
  const [includeCsv, setIncludeCsv] = useState(false);  // maps to include_usernames
  const [pushToJira, setPushToJira] = useState(false);
  const [detectLookalike, setDetectLookalike] = useState(false);
  const [pushToSlack, setPushToSlack] = useState(false);
  const [includePasswords, setIncludePasswords] = useState(false);
  const [adding, setAdding] = useState(false);

  // Optional forbidden modal
  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  // JWT getter
  function getJwtToken() {
    try {
      return (
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  // Put this near your other helpers (it uses getJwtToken and redirectToLogin)
async function deleteWatchlist(itemOrId, rows = []) {
  const isObj = itemOrId && typeof itemOrId === "object";
  const id = isObj ? itemOrId.id : itemOrId;

  const row = isObj ? itemOrId : (rows.find(x => String(x.id) === String(id)) || null);

  const entry_type = row?.entry_type || row?.type || row?.kind || null;
  const entry = row?.entry || row?.value || row?.identifier || null;

  if (!entry_type || !entry) {
    throw new Error(
      "Missing entry_type/entry. Call deleteWatchlist(row), or pass the row array as the 2nd arg when calling deleteWatchlist(id, rows)."
    );
  }

  const token = getJwtToken();
  if (!token) throw new Error("Authentication required.");

  const res = await fetch(
    "https://whiteintel.io/api/regular/app/v2/watchlist/delete_watchlist_item.php",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ entry_type, entry }),
    }
  );

  if ([401, 419, 440].includes(res.status)) {
    redirectToLogin();
    throw new Error(`AUTH_REDIRECT_${res.status}`);
  }
  if (res.status === 403) {
    throw new Error("You don’t have permission to delete this item.");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Delete failed (${res.status})`);
  }

  const data = await res.json().catch(() => ({}));
  if (data?.success === false) {
    throw new Error(data?.message || "Delete failed.");
  }

  // Return API message so caller can show it to the user
  return {
    success: true,
    id,
    entry_type,
    entry,
    message: data?.message || "Watchlist item deleted.",
  };
}

const handleDelete = async (row) => {
  setListError(null);
  setListSuccess(null);
  setDeletingId(row?.id ?? null);

  try {
    const result = await deleteWatchlist(row); // or deleteWatchlist(row.id, rows)
    // remove from UI (prefer id, fallback to entry+type)
    setRows(prev =>
      prev.filter(x =>
        x.id != null
          ? String(x.id) !== String(row.id)
          : !(x.entry === row.entry && x.entry_type === row.entry_type)
      )
    );
    setListSuccess(result.message || "Watchlist item deleted and credit restored");
  } catch (e) {
    setListError(e.message || "Failed to delete watchlist item");
  } finally {
    fetchCredits();
    setDeletingId(null);
  }
};

  // Fetch watchlist items
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const jwtToken = getJwtToken();

        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwtToken}`,
            },
            body: JSON.stringify({ metric: "all" }),
          }
        );

        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();

        // Expected: array of { entry, entry_type, status, added_at } (may include id)
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setListError(e.message || "Unable to load watchlist items");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

 const fetchCredits = async () => {
    try {
      const jwtToken = getJwtToken();
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ metric: "tokens" }),
        }
      );
      if (!res.ok) throw new Error("Failed to fetch tokens");
      const data = await res.json();
      // API returns [{ domain_tokens, email_tokens, computername_tokens, ip_tokens }]
      const obj = Array.isArray(data) ? data[0] : data;
      setCredits(obj || {});
    } catch (e) {
      console.error("Fetch credits error:", e);
    }
  };
useEffect(() => {

  fetchCredits();
}, []);

const jiraReady = Boolean(credits?.jira_active);
const slackReady = Boolean(credits?.slack_active);
const csvAllowed = Boolean(credits?.csv_allowed ?? false);
const lookalikeAllowed = Boolean(credits?.lookalike_allowed ?? false);
// if an integration becomes inactive, clear its toggle
useEffect(() => {
  if (!jiraReady && pushToJira) setPushToJira(false);
}, [jiraReady]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  if (!slackReady && pushToSlack) setPushToSlack(false);
}, [slackReady]); // eslint-disable-line react-hooks/exhaustive-deps
  // Filtered list
  const filtered = useMemo(() => {
    return rows
      .filter((r) => (status === "all" ? true : r.status === status))
      .filter((r) => (type === "all" ? true : r.entry_type === type))
      .filter((r) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return r.entry?.toLowerCase().includes(q);
      })
      .sort((a, b) => (b.added_at || "").localeCompare(a.added_at || ""));
  }, [rows, status, type, query]);

  // Toggle status (client-side)
const toggleRowStatus = async (id, currentStatus) => {
  const newStatus = currentStatus === "enabled" ? "disabled" : "enabled";

  // optimistic UI update
  setRows((prev) =>
    prev.map((r) =>
      r.id === id ? { ...r, status: newStatus } : r
    )
  );

  try {
    await fetch("https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwtToken()}`,
      },
      body: JSON.stringify({
        id,
        metric: "status_update",
        status: newStatus,
      }),
    });
  } catch (err) {
    console.error("Failed to update status:", err);
    // rollback on error
    setRows((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, status: currentStatus } : r
      )
    );
  }
};



  const resetForm = () => {
    setNewEntry("");
    setRecordType("both");
    setIgnored("");
    setIncludeCsv(false);
    setIncludePasswords(false);
    setPushToJira(false);
    setPushToSlack(false);
  };

  // ---- Add Identifier (API wired) ----
  const addIdentifier = async () => {
    const searchQuery = newEntry.trim();
    if (!searchQuery) return;

    // Map recordType → booleans
    const corporateAlerts = recordType === "both" || recordType === "corporate";
    const consumerAlerts  = recordType === "both" || recordType === "consumer";
    
    const payload = {
      query: searchQuery,
      metric: newType, // domain | email | ip | computername | keyword
      corporate_alerts: corporateAlerts,
      consumer_alerts: consumerAlerts,
      include_usernames: includeCsv,
      push_to_jira: pushToJira,
      push_to_slack: pushToSlack,
      detect_look_alike: detectLookalike,
      notify_email: ignored, // backend will fallback to user email if invalid/empty
    };

    setAdding(true);
    setError(null);
    setSuccess(null);

    try {
      const jwtToken = getJwtToken();

      const response = await fetch(
        "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_manage_handler_v2.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      // Special-case 403 Forbidden search
      if (response.status === 403) {
        if (responseData?.message?.includes("Forbidden search")) {
          setIsForbiddenModalOpen(true);
          return;
        }
      }

      if (!response.ok) {
        const msg =
          responseData?.error ||
          responseData?.message ||
          `Request failed (${response.status})`;
        setError(msg);
        return;
      }

      // Success: prepend optimistically (in case API doesn't echo the new row)
      const newRow = {
        id: responseData?.id, // if backend returns it
        entry: searchQuery,
        entry_type: newType,
        status: "enabled",
        added_at: new Date().toISOString().slice(0, 19).replace("T", " "),
      };
      setRows((prev) => [newRow, ...prev]);
      resetForm();
      fetchCredits();
      setSuccess(responseData?.success || "Entry added to your watchlist.");
    } catch (e) {
      console.error("Add identifier error:", e);
      setError("A network error occurred. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer to align with Sidebar */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Watchlists</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        {/* Content */}
        <main className="flex-1 px-4 md:px-6 pt-4 pb-10">
          {/* Alert banners */}
{listError && (
  <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[13px] text-red-200 flex items-start justify-between">
    <span className="pr-4">{listError}</span>
    <button
      onClick={() => setListError(null)}
      className="ml-3 text-[11px] text-neutral-400 hover:text-white"
      aria-label="Dismiss error"
      type="button"
    >
      Dismiss
    </button>
  </div>
)}

{listSuccess && (
  <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[13px] text-emerald-200 flex items-start justify-between">
    <span className="pr-4">{listSuccess}</span>
    <button
      onClick={() => setListSuccess(null)}
      className="ml-3 text-[11px] text-neutral-400 hover:text-white"
      aria-label="Dismiss success"
      type="button"
    >
      Dismiss
    </button>
  </div>
)}

 

          {/* Two columns */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-4">
            {/* LEFT: list (search + table). Same height as the right panel
                so the grid is rigid and tab-switching never reflows. */}
            <div className={`${panel} flex flex-col lg:h-[78vh]`}>
              {/* Search bar + filter pills — flat, no nested frosted shell. */}
              <div className="px-4 pt-4 pb-3 border-b border-sky-900/30">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Search input — takes remaining space */}
                  <div className="relative flex-1 min-w-[180px]">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search identifiers…"
                      className="w-full rounded-md border border-sky-900/30 bg-neutral-900/60 pl-9 pr-3 py-2 text-[13px] outline-none focus:border-sky-500/50 placeholder-neutral-500 transition-colors"
                    />
                  </div>

                  {/* Status filter — cycles enabled → disabled → all */}
                  <div className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/30 bg-neutral-900/60 px-2.5 py-1.5 text-[12px]">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">Status</span>
                    <button
                      type="button"
                      onClick={() =>
                        setStatus((s) =>
                          s === "enabled" ? "disabled" : s === "disabled" ? "all" : "enabled"
                        )
                      }
                      className="inline-flex items-center gap-1 text-white hover:text-sky-300 transition-colors"
                    >
                      {status === "all" ? "All" : status[0].toUpperCase() + status.slice(1)}
                      <ChevronDown className="h-3 w-3 text-neutral-500" />
                    </button>
                  </div>

                  {/* Type filter — native select for the longer option list */}
                  <div className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/30 bg-neutral-900/60 px-2.5 py-1.5 text-[12px]">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">Type</span>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="bg-transparent text-white outline-none text-[12px]"
                    >
                      <option value="all">All</option>
                      {TYPE_OPTIONS.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
                    {loading ? "Loading…" : `${filtered.length.toLocaleString()} ${filtered.length === 1 ? "identifier" : "identifiers"}`}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">Newest first</span>
                </div>
              </div>

              <div className="flex-1 overflow-auto">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-[#0a1224]/95 backdrop-blur-sm">
                    <tr className="text-neutral-500 border-b border-sky-900/30">
                      <th className="px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">Entry</th>
                      <th className="px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">Type</th>
                      <th className="px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">Created</th>
                      <th className="px-4 py-2.5 text-right font-semibold uppercase tracking-wider text-[10px]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sky-900/20">
                    {listError ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-10 text-center text-red-400">{listError}</td>
                      </tr>
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-10 text-center text-neutral-500">
                          No identifiers match your filters.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r, idx) => (
                        <tr key={`${r.entry}-${idx}`} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-4 py-2.5">
                            {/* On mobile the pill eats too much of the narrow
                                column and forces the entry to wrap one char
                                per line, so stack vertically below sm. */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
                              <div className="text-white font-mono break-all min-w-0">{r.entry}</div>
                              <span
                                className={`self-start sm:self-auto shrink-0 inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                                  r.status === "enabled"
                                    ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
                                    : "border-amber-500/30 text-amber-300 bg-amber-500/10"
                                }`}
                              >
                                {r.status === "enabled" ? "Enabled" : "Disabled"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-neutral-400 capitalize font-mono">{r.entry_type}</td>
                          <td className="px-4 py-2.5 text-neutral-400 font-mono tabular-nums">{r.added_at}</td>
                          <td className="px-4 py-2.5 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                className="text-[11px] uppercase tracking-wider font-mono text-neutral-400 hover:text-sky-300 px-2 py-1 rounded transition-colors"
                                onClick={() => toggleRowStatus(r.id, r.status)}
                                title={r.status === "enabled" ? "Disable" : "Enable"}
                              >
                                {r.status === "enabled" ? "Disable" : "Enable"}
                              </button>
                              <button
                                className="rounded-md p-1 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 disabled:opacity-50 transition-colors"
                                title="Delete"
                                onClick={() => handleDelete(r)}
                                disabled={deletingId === r.id}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                              <button
                                className="rounded-md p-1 text-neutral-400 hover:bg-white/[0.06] hover:text-white transition-colors"
                                title="Details"
                                onClick={() => {
                                  setSelectedId(r.id);
                                  setSidebarOpen(true);
                                }}
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* RIGHT: Create Identifier panel — fixed height + internal scroll
                so tab switches never resize the card. */}
          <div className={`${panel} flex flex-col lg:h-[78vh]`}>
      {/* Underline tab strip — matches Profile / Organizations. */}
      <div className="flex items-stretch border-b border-sky-900/30 px-2">
        {[
          { id: "settings",    label: "Settings" },
          { id: "definitions", label: "Definitions" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`relative px-3 py-3 text-[12px] font-semibold uppercase tracking-wider transition-colors ${
              activeTab === t.id ? "text-white" : "text-neutral-500 hover:text-neutral-300"
            }`}
          >
            {t.label}
            {activeTab === t.id && (
              <span className="absolute inset-x-2 -bottom-px h-0.5 bg-sky-400" />
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-5">
        {error && (
          <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[13px] text-red-200 flex items-start justify-between">
            <span className="pr-4">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-3 text-[11px] text-neutral-400 hover:text-white"
              aria-label="Dismiss error"
              type="button"
            >
              Dismiss
            </button>
          </div>
        )}

        {success && (
          <div className="mb-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[13px] text-emerald-200 flex items-start justify-between">
            <span className="pr-4">{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="ml-3 text-[11px] text-neutral-400 hover:text-white"
              aria-label="Dismiss success"
              type="button"
            >
              Dismiss
            </button>
          </div>
        )}

      {/* Tab Content */}
      {activeTab === "settings" && (
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">Create identifier</div>
            <div className="text-[12px] text-neutral-500">Add a new asset to your watchlist.</div>
          </div>

          {/* Subscription usage */}
<div className="rounded-md border border-sky-900/30 bg-neutral-900/40 px-3 py-2.5 text-[12px] text-neutral-400">
  {credits ? (
    <>
      {newType === "domain" && (
        <span>
          {credits.domain_tokens} Domain identifiers left in your subscription.
          {credits.domain_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "email" && (
        <span>
          {credits.email_tokens} Email identifiers left in your subscription.
          {credits.email_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "ip" && (
        <span>
          {credits.ip_tokens} IP identifiers left in your subscription.
          {credits.ip_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "computername" && (
        <span>
          {credits.computername_tokens} Computer Name identifiers left in your subscription.
          {credits.computername_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

      {newType === "keyword" && (
         <span>
          {credits.keyword_tokens ?? 0} Keyword identifiers left in your subscription.
          {credits.keyword_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}

            {newType === "github_repo" && (
         <span>
          {credits?.git_tokens ?? 0} Github Repository  identifiers left in your subscription.
          {credits?.git_tokens === 0 && (
            <> <a href="/upgrades" className="underline">Upgrade your license to access more.</a></>
          )}
        </span>
      )}
    </>
  ) : (
    "Loading subscription usage..."
  )}
</div>


          {/* Type + Identifier value on the same row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={fieldLabel}>Type</label>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className={inputClass}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={fieldLabel}>Identifier</label>
              <input
                value={newEntry}
                onChange={(e) => setNewEntry(e.target.value)}
                placeholder={placeholders[newType] ?? "Enter value"}
                className={inputClass}
              />
            </div>
          </div>

          {/* Record / Audience type — only for domains */}
          {newType === "domain" && (
            <div>
              <label className={fieldLabel}>Record type</label>
              <select
                value={recordType}
                onChange={(e) => setRecordType(e.target.value)}
                className={inputClass}
              >
                {DATA_OPTIONS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Who to notify */}
          <div>
            <label className={fieldLabel}>
              Notify <span className="text-neutral-600 normal-case">(your email if empty)</span>
            </label>
            <input
              value={ignored}
              onChange={(e) => setIgnored(e.target.value)}
              placeholder="user@company.com"
              className={inputClass}
            />
          </div>

          {/* Delivery options */}
          <div>
            <div className={fieldLabel}>Delivery options</div>
            <div className="space-y-1.5 mt-1">

                    {/*Look alike domains*/}

                    {newType === "domain" && (
                    <label
  className={`flex items-center gap-2 text-[13px] ${
    lookalikeAllowed ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={lookalikeAllowed ? "" : "Slack integration not configured"}
>
  <input
    type="checkbox"
    checked={detectLookalike}
    onChange={(e) => setDetectLookalike(e.target.checked)}
    disabled={!lookalikeAllowed}
    aria-disabled={!lookalikeAllowed}
    className={`h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500
      ${lookalikeAllowed ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
  />
  <span>
    Detect Lookalike Domains
    {!lookalikeAllowed && <span className="ml-1 text-neutral-400">(Higher license required.)</span>}
  </span>
</label>
                             )}         {/* Jira */}

{newType !== "keyword" && (
<label
  className={`flex items-center gap-2 text-[13px] ${
    jiraReady ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={jiraReady ? "" : "Jira integration not configured"}
>
  <input
    type="checkbox"
    checked={pushToJira}
    onChange={(e) => setPushToJira(e.target.checked)}
    disabled={!jiraReady}
    aria-disabled={!jiraReady}
    className={`h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500
      ${jiraReady ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
  />
  <span>
    Push to Jira
    {!jiraReady && <span className="ml-1 text-neutral-400">(Not configured yet)</span>}
  </span>
</label>
)}
{/* Slack */}
<label
  className={`flex items-center gap-2 text-[13px] ${
    slackReady ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={slackReady ? "" : "Slack integration not configured"}
>
  <input
    type="checkbox"
    checked={pushToSlack}
    onChange={(e) => setPushToSlack(e.target.checked)}
    disabled={!slackReady}
    aria-disabled={!slackReady}
    className={`h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500
      ${slackReady ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
  />
  <span>
    Push to Slack
    {!slackReady && <span className="ml-1 text-neutral-400">(Not configured yet)</span>}
  </span>
</label>
{newType !== "keyword" && newType !== "git_repo" && (
     <label
  className={`flex items-center gap-2 text-[13px] ${
    csvAllowed ? "text-neutral-300 cursor-pointer" : "text-neutral-500 cursor-not-allowed"
  }`}
  title={csvAllowed ? "" : "Higher license required."}
>
              <input
                type="checkbox"
                checked={includeCsv}
                onChange={(e) => setIncludeCsv(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-700 bg-neutral-900 text-sky-500"
              />
              Include usernames as CSV
              {!csvAllowed && <span className="ml-1 text-neutral-400">(Higher license required.)</span>}
            </label>
)}
            </div>
          </div>


          {/* Actions */}
          <div className="flex justify-between pt-3 border-t border-sky-900/20">
            <button
              onClick={resetForm}
              className={secondaryBtn}
            >
              Reset
            </button>
            <button
              onClick={addIdentifier}
              disabled={!newEntry.trim() || adding}
              className={primaryBtn}
            >
              {adding ? "Adding…" : "Add identifier"}
            </button>
          </div>


        </div>
      )}

      {activeTab === "definitions" && (
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">Identifier definitions</div>
            <div className="text-[12px] text-neutral-500">What each monitoring asset type covers, and which license tier unlocks it.</div>
          </div>

          {[
            { name: "Domain",         body: <>A registrable domain (e.g., <code className="font-mono text-neutral-200">acme.com</code>). Triggers on credential leaks against the website or any account using that email domain (e.g., user@acme.com).</> },
            { name: "Email address",  body: <>An individual mailbox to track against infostealer infections and credential leaks.</> },
            { name: "Look-alike domain", body: <>A domain intentionally registered to resemble your brand (e.g., <code className="font-mono text-neutral-200">micros0ft.com</code>). Used for phishing, impersonation, and malware delivery.</>, license: "Enterprise / Threat Intelligence" },
            { name: "IP",             body: <>An IPv4 or IPv6 address to monitor for infostealer infections.</>, license: "Threat Intelligence" },
            { name: "Computer name",  body: <>A device hostname (e.g., <code className="font-mono text-neutral-200">DESKTOP-4KSA</code>) to monitor for infostealer infections.</>, license: "Threat Intelligence" },
            { name: "Keyword",        body: <>Free-text term to monitor on dark-web mentions (e.g., your company or product name).</> },
            { name: "Consumer records", body: <>Leaked accounts <span className="text-neutral-300">without</span> a corporate email extension (e.g., user@gmail.com).</> },
            { name: "Corporate records", body: <>Leaked accounts <span className="text-neutral-300">with</span> your corporate email extension (e.g., user@acme.com).</> },
          ].map(({ name, body, license }) => (
            <div key={name} className="rounded-md border border-sky-900/30 bg-neutral-900/40 px-3 py-2.5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[12px] font-semibold text-sky-300">{name}</span>
                {license && (
                  <span className="inline-flex items-center rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-amber-300">
                    {license}
                  </span>
                )}
              </div>
              <div className="text-[12px] text-neutral-400 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
          </div>

        </main>
            <WatchlistItemSidebar
        open={sidebarOpen}
        itemId={selectedId}
        jwtToken={getJwtToken()}
        onClose={() => setSidebarOpen(false)}
        onStatusChange={(id, status) => {
            setRows((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, status } : item
            )
            );
        }}
        />
      </div>

      {/* Minimal Forbidden modal */}
      {isForbiddenModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setIsForbiddenModalOpen(false)}
          />
          <div className="relative z-[201] max-w-md w-full rounded-md border border-red-500/30 bg-[#000414] p-5 text-sm">
            <div className="text-red-300 font-semibold mb-2">Forbidden search</div>
            <div className="text-neutral-200">
              This identifier is not allowed to be added. Please contact support if you believe this is an error.
            </div>
            <div className="mt-4 text-right">
              <button
                onClick={() => setIsForbiddenModalOpen(false)}
                className="rounded-lg border border-neutral-700 bg-neutral-900/40 px-3 py-1.5 text-neutral-200 hover:bg-neutral-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
